import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Check } from "lucide-react";
import { api, formatApiError, resolveMediaUrl } from "@/lib/api";
import RichTextEditor from "@/components/admin/RichTextEditor";
import FileUpload from "@/components/admin/FileUpload";
import { asBi, bi, biSet, emptyBi } from "@/lib/bilingual";
import { clearCache } from "@/lib/publicCache";

const empty = {
  title: emptyBi(),
  slug: "",
  excerpt: emptyBi(),
  content: emptyBi(),
  thumbnail: "",
  category: emptyBi("Insight", "Insight"),
  tags: [],
  author: "Admin",
  seo_title: emptyBi(),
  seo_description: emptyBi(),
  published: true,
};

const AdminArticles = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [editLang, setEditLang] = useState("id");

  const load = () => api.get("/articles").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing("new"); setForm(empty); setEditLang("id"); };
  const openEdit = (a) => {
    setEditing(a.id);
    setEditLang("id");
    setForm({
      ...a,
      title: asBi(a.title),
      excerpt: asBi(a.excerpt),
      content: asBi(a.content),
      category: asBi(a.category, "Insight"),
      seo_title: asBi(a.seo_title),
      seo_description: asBi(a.seo_description),
      tags: a.tags || [],
    });
  };

  const save = async () => {
    try {
      const payload = {
        ...form,
        title: form.title,
        excerpt: form.excerpt || emptyBi(),
        content: form.content || emptyBi(),
        category: form.category,
        seo_title: form.seo_title || emptyBi(),
        seo_description: form.seo_description || emptyBi(),
      };
      if (editing === "new") await api.post("/articles", payload);
      else await api.put(`/articles/${editing}`, payload);
      clearCache("/articles", { published: true });
      clearCache("/articles");
      toast.success("Berhasil disimpan");
      setEditing(null); setForm(empty); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail || e.response?.data)); }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus artikel ini?")) return;
    await api.delete(`/articles/${id}`);
    clearCache("/articles", { published: true });
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-display font-bold">Artikel</h1>
          <p className="mt-1 text-sm text-zinc-500">Kelola artikel &amp; insight blog (Indonesia &amp; English).</p>
        </div>
        <button onClick={openNew} data-testid="new-article" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-maroon-800 text-white font-semibold">
          <Plus size={16} /> Tambah Artikel
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500">
            <tr><th className="p-4">Judul</th><th className="p-4">Kategori</th><th className="p-4">Publish</th><th className="p-4"></th></tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id} className="border-t border-zinc-100">
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    {a.thumbnail && <img src={resolveMediaUrl(a.thumbnail)} alt={bi(a.title, "id")} className="w-10 h-10 rounded-lg object-cover" />}
                    <div>
                      <div className="font-medium text-zinc-900">{bi(a.title, "id")}</div>
                      {bi(a.title, "en") && bi(a.title, "en") !== bi(a.title, "id") && (
                        <div className="text-xs text-zinc-500">{bi(a.title, "en")}</div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="p-4 text-zinc-600">{bi(a.category, "id")}</td>
                <td className="p-4">{a.published ? <span className="text-maroon-700 font-semibold">Aktif</span> : <span className="text-zinc-400">Draft</span>}</td>
                <td className="p-4 text-right">
                  <button onClick={() => openEdit(a)} className="p-2 rounded-lg hover:bg-zinc-100"><Pencil size={14} /></button>
                  <button onClick={() => del(a.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-600"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={4} className="p-10 text-center text-zinc-500">Belum ada artikel.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-4xl max-h-[92vh] overflow-y-auto">
            <div className="sticky top-0 bg-white flex items-center justify-between p-6 border-b border-zinc-200 z-10">
              <h3 className="text-xl font-display font-bold">{editing === "new" ? "Tambah Artikel" : "Edit Artikel"}</h3>
              <div className="flex items-center gap-2">
                <div className="flex rounded-full overflow-hidden border border-zinc-200 text-xs font-bold">
                  <button type="button" onClick={() => setEditLang("id")} className={`px-3 py-1.5 ${editLang === "id" ? "bg-maroon-800 text-white" : "bg-white text-zinc-600"}`}>ID</button>
                  <button type="button" onClick={() => setEditLang("en")} className={`px-3 py-1.5 ${editLang === "en" ? "bg-maroon-800 text-white" : "bg-white text-zinc-600"}`}>EN</button>
                </div>
                <button onClick={() => setEditing(null)} className="p-2 rounded-lg hover:bg-zinc-100"><X size={18} /></button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <Input label={`Judul * (${editLang.toUpperCase()})`} value={bi(form.title, editLang)} onChange={(v) => setForm({ ...form, title: biSet(form.title, editLang, v) })} testid="af-title" />
              <div className="grid sm:grid-cols-2 gap-4">
                <Input label={`Kategori (${editLang.toUpperCase()})`} value={bi(form.category, editLang)} onChange={(v) => setForm({ ...form, category: biSet(form.category, editLang, v) })} />
                <Input label="Author" value={form.author} onChange={(v) => setForm({ ...form, author: v })} />
              </div>
              <FileUpload label="Thumbnail" value={form.thumbnail} onChange={(v) => setForm({ ...form, thumbnail: v })} folder="articles" aspect="wide" testid="af-thumbnail-upload" />
              <div>
                <label className="block text-sm font-semibold text-zinc-800 mb-2">Excerpt ({editLang.toUpperCase()})</label>
                <textarea rows={2} value={bi(form.excerpt, editLang)} onChange={(e) => setForm({ ...form, excerpt: biSet(form.excerpt, editLang, e.target.value) })}
                  className="w-full px-4 py-3 rounded-xl border border-zinc-200 focus:border-maroon-700 outline-none" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-zinc-800 mb-2">Konten Artikel ({editLang.toUpperCase()})</label>
                <RichTextEditor value={bi(form.content, editLang)} onChange={(v) => setForm({ ...form, content: biSet(form.content, editLang, v) })} />
              </div>
              <Input label="Tags (pisah dengan koma)" value={(form.tags || []).join(", ")} onChange={(v) => setForm({ ...form, tags: v.split(",").map((t) => t.trim()).filter(Boolean) })} />
              <div className="grid sm:grid-cols-2 gap-4">
                <Input label={`SEO Title (${editLang.toUpperCase()})`} value={bi(form.seo_title, editLang)} onChange={(v) => setForm({ ...form, seo_title: biSet(form.seo_title, editLang, v) })} />
                <Input label={`SEO Description (${editLang.toUpperCase()})`} value={bi(form.seo_description, editLang)} onChange={(v) => setForm({ ...form, seo_description: biSet(form.seo_description, editLang, v) })} />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} id="apub" />
                <label htmlFor="apub" className="text-sm">Publish artikel</label>
              </div>
            </div>
            <div className="sticky bottom-0 bg-white flex justify-end gap-3 p-6 border-t border-zinc-200">
              <button onClick={() => setEditing(null)} className="px-5 py-2.5 rounded-full border border-zinc-200 font-semibold">Batal</button>
              <button onClick={save} data-testid="save-article" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-maroon-800 text-white font-semibold"><Check size={16} /> Simpan</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Input = ({ label, value, onChange, testid }) => (
  <div>
    <label className="block text-sm font-semibold text-zinc-800 mb-2">{label}</label>
    <input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-full px-4 py-3 rounded-xl border border-zinc-200 focus:border-maroon-700 focus:ring-2 focus:ring-maroon-100 outline-none" />
  </div>
);

export default AdminArticles;
